import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import InputMask from 'react-input-mask';
import { get, debounce } from 'lodash';
import {
  Box,
  FlexBox,
  Typography,
  AbsoluteBox,
  InputField,
  CircularProgress,
  Grid,
  MuiBox,
  Icon,
  textSize,
  color
} from '@kis-technologies/kis-ui';
import {
  selectors as configSelectors,
  Configuration,
  SurveyField,
  SurveyItem,
  LocalizedStrRecord
} from 'services/configuration';
import { actions as customerActions, selectors as customerSelectors } from 'services/customer';
import { selectors as cartSelectors, Cart, Item } from 'services/cart';
import { actions as countryAction, selectors as countrySelectors, Country } from 'services/countries';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { Keyboard } from 'components/Keyboard';
import { NumPadAction } from 'components/NumPad';
import { Scrollbar } from 'components/Scrollbar';
import { Footer } from 'components/Footer';
import { ActionOverlay } from 'components/ActionOverlay';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { PhoneNumberInput, Country as PhoneCountry } from 'components/PhoneNumberInput';
import { SelectDropdown } from 'components/SelectDropdown';
import { validateInput, validateInputType } from 'utils/validation';
import { useStyles } from './styled';

const SurveyView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locationRoute = useLocation();
  const navigate = useNavigate();
  const state = locationRoute.state as { from?: string };

  // Action Creator and Selectors
  const { saveSurvey, resetError, getCountries, getLocations, resetLocations, setPhoneCountry } = useActions({
    saveSurvey: customerActions.saveSurvey,
    resetError: customerActions.resetError,
    getCountries: countryAction.getCountries,
    getLocations: customerActions.getLocations,
    resetLocations: customerActions.resetLocations,
    setPhoneCountry: customerActions.setPhoneCountry
  });

  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const survey = useSelector(customerSelectors.selectSurvey);
  const loading = useSelector(customerSelectors.selectLoading);
  const surveyError = useSelector(customerSelectors.selectError);
  const cart = useSelector(cartSelectors.selectCart) as Cart;
  const donationProductInCart = useSelector(cartSelectors.selectDonationProductInCart);
  const countries = useSelector(countrySelectors.selectCountries);
  const locations = useSelector(customerSelectors.selectLocations);
  const countryError = useSelector(countrySelectors.selectError);
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);
  const customer = useSelector(customerSelectors.selectCustomer);
  const lookupData = useSelector(customerSelectors.selectLookupData);
  const phoneCountry = useSelector(customerSelectors.selectPhoneCountry);

  const surveyConfig = get(config, `${ticketingPath}.survey`);
  const priorityCountries: string[] = get(config, `${ticketingPath}.priorityPostalCodeLookupCountries`, []);
  const surveyEnabled = get(surveyConfig, 'enabled', false);
  const surveyList: SurveyItem[] = get(surveyConfig, 'surveyList', []);
  const emailSuggestions: string[] = get(config, `messaging.${i18n.language}.emailSuggestions.buttons`, [
    '@gmail.com',
    '@yahoo.com',
    '@outlook.com'
  ]);

  const [formData, setFormData] = React.useState<Record<string, string>>({});
  const [error, setError] = React.useState<Record<string, string>>({});
  const formRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [displayFields, setDisplayFields] = React.useState<SurveyField[]>([]);
  const [header, setHeader] = React.useState();
  const [subheader, setSubheader] = React.useState();
  const [inputControl, setInputControl] = React.useState('');
  const [apiKey, setAPIKey] = React.useState('');
  const [selectedField, setSelectedField] = React.useState<SurveyField | null>(null);
  const [inputIndex, setInputIndex] = React.useState(-1);
  const [validation, setValidation] = React.useState(false);
  const [validationComplete, setValidationComplete] = React.useState(false);
  const [hideScrollBar, setHideScrollBar] = React.useState(false);
  const [skipAndContinue, setSkipAndContinue] = React.useState(true);
  const [emptySurvey, setEmptySurvey] = React.useState(true);
  const [uiLoading, setUILoading] = React.useState(true);
  const [locationsLoading, setLocationsLoading] = React.useState(false);
  const [prefilledFields, setPrefilledFields] = React.useState<number[]>([]);
  const [phoneNumberInput, setPhoneNumberInput] = React.useState<{ keyboard: string; selectedCountry?: PhoneCountry }>({
    keyboard: '',
    selectedCountry: phoneCountry
  });
  const [countryOptions, setCountryOptions] = React.useState<string[]>([]);
  const [stateOptions, setStateOptions] = React.useState<string[]>([]);
  const [cityOptions, setCityOptions] = React.useState<string[]>([]);
  const [selectedCountryOption, setSelectedCountryOption] = React.useState<string>('');
  const [selectedStateOption, setSelectedStateOption] = React.useState<string>('');
  const [selectedCityOption, setSelectedCityOption] = React.useState<string>('');
  const [locationFieldsDisabled, setLocationFieldsDisabled] = React.useState<boolean>(true);

  const nextText = skipAndContinue && emptySurvey ? t('commonText.skip') : t('commonText.continue');
  const classes = useStyles({ inputControl })();

  // INITIAL OBJECT METHODS
  const getReplacedHeader = (labelObj: Partial<Record<string, string>> = {}, cartItem: Item) => {
    const replacedLabelObj = { ...labelObj };
    let labelTxt = get(replacedLabelObj, `${i18n.language}`) || '';
    const cartItemProduct = cart.products.find(
      (product) => product.items && product.items.find((item) => item.id === cartItem.id)
    );
    labelTxt = labelTxt.replace('{{performanceName}}', (cartItemProduct && cartItemProduct.name) || '');
    labelTxt = labelTxt.replace('{{priceTypeName}}', cartItem.type);
    replacedLabelObj[i18n.language] = labelTxt;

    return replacedLabelObj;
  };

  /**
   * Returns the cart items of dependency, if any
   * @param {Object} cartDependencies - cart dependency config
   */
  const getSurveyRequiredItemsInCart = (cartDependencies?: {
    event?: string[];
    genre?: string[];
    package?: string[];
  }) => {
    const cartItems: Item[] = [];
    if (
      cart &&
      cart.products &&
      cartDependencies &&
      ((cartDependencies.genre && cartDependencies.genre.length) ||
        (cartDependencies.event && cartDependencies.event.length) ||
        (cartDependencies.package && cartDependencies.package.length))
    ) {
      cart.products.forEach((product) => {
        if (product.items) {
          if (
            (cartDependencies.genre && cartDependencies.genre.includes(product.genre || '')) ||
            (cartDependencies.event && cartDependencies.event.includes(product.name || ''))
          ) {
            cartItems.push(...product.items);
          }
        } else if (product.subProducts) {
          if (cartDependencies.package && cartDependencies.package.includes(product.name || '')) {
            if (product.subProducts[0] && product.subProducts[0].items) {
              cartItems.push(...product.subProducts[0].items);
            }
          } else {
            product.subProducts.forEach((subProduct) => {
              if (subProduct.items) {
                if (
                  (cartDependencies.genre && cartDependencies.genre.includes(subProduct.genre || '')) ||
                  (cartDependencies.event && cartDependencies.event.includes(subProduct.name || ''))
                ) {
                  cartItems.push(...subProduct.items);
                }
              }
            });
          }
        }
      });
    }

    return cartItems;
  };

  const getSubSurveyDisplayFields = (subSurveyFields: SurveyField[], surveyItems: Item[], surveyApiParam: string) => {
    const items: SurveyField[] = [];
    const filteredFields = subSurveyFields.filter((subSurveyItem) => subSurveyItem.surveyApiParam === surveyApiParam);
    surveyItems.forEach((cartItem, cartIdx) => {
      filteredFields.forEach((filteredItem) => {
        if (filteredItem.inputType === 'header') {
          const headerText = getReplacedHeader(filteredItem.label, cartItem);
          items.push({ ...filteredItem, label: headerText, apiParam: filteredItem.apiParam + cartIdx });
        } else {
          items.push({ ...filteredItem, apiParam: filteredItem.apiParam + cartIdx });
        }
      });
    });

    return items;
  };

  const checkSurveyEmpty = () => {
    if (formData && Object.keys(formData).length) {
      let nonEmptyFields = 0;
      Object.values(formData).forEach((value) => {
        if (value) nonEmptyFields++;
      });
      setEmptySurvey(!!!nonEmptyFields);
      setValidation(!!!nonEmptyFields);
    }
  };

  // Reset data when the component mounts or re-renders
  useEffect(() => {
    resetLocations();
  }, []);

  // if there's a valid (uncompleted) survey: setDisplayFields
  // if no survey or already completed: navigate forward/backward
  useEffect(() => {
    let tempFields: SurveyField[] = [];
    if (surveyEnabled && !survey && surveyList && surveyList.length) {
      surveyList.every((surveyListItem) => {
        const surveyFields = (get(surveyListItem, `surveyFields`) as SurveyField[]) || [];
        const subSurveyFields = (get(surveyListItem, `subSurveyFields`) as SurveyField[]) || [];
        const cartDependencies = get(surveyListItem, 'cartDependencies');

        // survey with a dependency of an event, genre or package
        if (cartDependencies && (cartDependencies.event || cartDependencies.genre || cartDependencies.package)) {
          const surveyItemsInCart = getSurveyRequiredItemsInCart(cartDependencies);
          if (surveyItemsInCart && surveyItemsInCart.length) {
            surveyFields.forEach((surveyItem) => {
              if (surveyItem.inputType === 'sub-survey') {
                const subSurveyItems: SurveyField[] = getSubSurveyDisplayFields(
                  subSurveyFields,
                  surveyItemsInCart,
                  surveyItem.apiParam
                );
                tempFields = tempFields.concat(subSurveyItems);
              } else {
                tempFields.push(surveyItem);
              }
            });
          }
          // survey with no dependency, OR a dependency of a donation
        } else if (!cartDependencies || donationProductInCart) {
          surveyFields.forEach((surveyItem) => {
            if (surveyItem.inputType === 'sub-survey') {
              let subSurveyItems: SurveyField[] = [];
              cart.products.forEach((product) => {
                if (product.subProducts && product.subProducts[0] && product.subProducts[0].items) {
                  subSurveyItems = subSurveyItems.concat(
                    getSubSurveyDisplayFields(subSurveyFields, product.subProducts[0].items, surveyItem.apiParam)
                  );
                } else if (product.items) {
                  subSurveyItems = subSurveyItems.concat(
                    getSubSurveyDisplayFields(subSurveyFields, product.items, surveyItem.apiParam)
                  );
                }
              });
              tempFields = tempFields.concat(subSurveyItems);
            } else {
              tempFields.push(surveyItem);
            }
          });
        }

        // Stop iterating the surveyList when displayFields are found
        if (tempFields.length) {
          setHeader(get(surveyListItem, `messages.header.${i18n.language}`, t('survey.surveyHeader')));
          setSubheader(get(surveyListItem, `messages.subheader.${i18n.language}`, t('survey.surveyMessage')));
          setDisplayFields(tempFields);

          return false;
        }
        return true;
      });
    }

    // no fields to show; navigate forward or backward
    if (!tempFields || !tempFields.length) {
      if (state && state.from === '/ticketing/cart/delivery-selection') {
        onPrevPageClick();
      } else {
        navigate('/ticketing/cart/delivery-selection');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey]);

  // after displayFields are set (indicates a valid survey)
  useEffect(() => {
    if (displayFields && displayFields.length) {
      const hasCountryField = displayFields.find((field) => field.inputType === 'countryName');
      if (hasCountryField && (!countries || !countries.length)) getCountries();

      // SET INITIAL SURVEY OBJECTS
      const apiParams = displayFields.map((item) => {
        if (skipAndContinue && item.required) {
          setSkipAndContinue(false);
        }
        return item.apiParam;
      });

      const initialFormRefs: Record<string, HTMLInputElement | null> = {};
      formRefs.current = apiParams.reduce((acc, key) => {
        acc[key] = null;
        return acc;
      }, initialFormRefs);

      const initialFormData: Record<string, string> = {};
      const tempRef = apiParams.reduce((acc, key) => {
        acc[key] = '';
        return acc;
      }, initialFormData);

      // Set initial form data and refs
      setError({ ...tempRef });

      // Prefill fields with either member | potentialMember | customer | lookupData
      const prefilledIndexes: number[] = [];
      if (tempRef) {
        Object.keys(tempRef).forEach((field, idx) => {
          if (customer && field in customer) {
            tempRef[field] = (customer as any)[field];
            prefilledIndexes.push(idx);
          } else if (lookupData && field in lookupData) {
            tempRef[field] = lookupData[field];
            prefilledIndexes.push(idx);
          }
        });

        if (tempRef.phone && phoneCountry) {
          const phoneNumber = tempRef.phone.split(phoneCountry.code)[1];
          if (phoneNumber) {
            setPhoneNumberInput({ keyboard: phoneNumber, selectedCountry: phoneCountry });
          }
        }
      }
      // Save the indexes, used to disable those fields
      if (prefilledIndexes && prefilledIndexes.length) {
        setEmptySurvey(false);
        setPrefilledFields(prefilledIndexes);
      }

      // Only setUILoading=false if not all fields were prefilled
      if (!prefilledIndexes || !prefilledIndexes.length || prefilledIndexes.length !== displayFields.length) {
        setUILoading(false);
      }

      setFormData(tempRef);
    }

    if (displayFields && displayFields.length === 1) {
      setAPIKey(displayFields[0].apiParam);
      setSelectedField(displayFields[0]);
      setInputIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayFields]);

  // if any data is prefilled: validate those fields
  useEffect(() => {
    if (prefilledFields && prefilledFields.length) {
      if (prefilledFields.length === displayFields.length) {
        validateForm();
      } else {
        prefilledFields.forEach((i) => validate(i));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledFields]);

  // if all fields prefilled: either submit survey or setUILoading=false based on the first validation
  // validationComplete never gets reset, so this should only fire after the first validation
  useEffect(() => {
    if (
      validationComplete &&
      prefilledFields &&
      prefilledFields.length &&
      prefilledFields.length === displayFields.length
    ) {
      if (validation) onNextPageClick();
      else setUILoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationComplete]);

  useEffect(() => {
    if (error[apiKey]) {
      setError({ ...error, [apiKey]: '' });
      const currIndex = displayFields.findIndex((item) => item.apiParam === apiKey);
      validate(currIndex);
    }
    if (skipAndContinue) {
      checkSurveyEmpty();
    }

    if (locations && selectedField && selectedField.apiParam === 'postalCode') {
      resetLocations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  // Update focus when the selected input field changes
  useEffect(() => {
    // Check if apiKey is valid, and prevent the effect from running if it's not.
    if (!apiKey || typeof apiKey !== 'string' || apiKey.trim() === '') {
      return;
    }

    // Get the actual HTMLInputElement from formRefs.current
    const selectedInput = formRefs.current[apiKey];

    // Only proceed if the selected input element actually exists (is not null)
    if (selectedInput) {
      setTimeout(() => {
        if (inputControl) {
          if (selectedInput.type === 'text') {
            selectedInput.setSelectionRange(selectedInput.value.length, selectedInput.value.length, 'forward');
          }
          selectedInput.focus();
        }
        const elementToScroll = selectedInput.lastElementChild || selectedInput;
        elementToScroll.scrollIntoView({ block: 'center' });
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputIndex, inputControl, apiKey]);

  useEffect(() => {
    if (selectedField) {
      setTimeout(() => {
        handleKeyBoardVisibility(selectedField.keyboard);
      }, 250);
    }
  }, [selectedField]);

  // Handle initial population of country options
  useEffect(() => {
    setLocationsLoading(false);
    setLocationFieldsDisabled(true);

    if (locations && locations.length === 0) {
      setError({ ...error, postalCode: t('validation.postalCodeNotFound') });
      setValidation(false);
    } else if (locations && locations.length > 0) {
      // Populate country options
      const uniqueCountries = [...new Set(locations.map((location) => location.country))];
      const sortedCountries = priorityCountries.length
        ? uniqueCountries.sort((countryA, countryB) => {
          const priorityCountryA = priorityCountries.indexOf(countryA);
          const priorityCountryB = priorityCountries.indexOf(countryB);

          if (priorityCountryA === -1) return 1;
          if (priorityCountryB === -1) return -1;

          return priorityCountryA - priorityCountryB;
        })
        : uniqueCountries;
      setCountryOptions(sortedCountries);

      // Set default country if options are available
      if (uniqueCountries.length > 0) {
        setAPIKey('country');
        const defaultCountry = uniqueCountries[0];
        setFormValueIfKeyExists(defaultCountry, 'country');
        setSelectedCountryOption(defaultCountry);
      }
    }
  }, [locations]);

  // Update states, or cities if 0 states, based on selected country
  useEffect(() => {
    if (selectedCountryOption && locations) {
      // Filter states based on selected country
      const filteredStates = [
        ...new Set(
          locations.filter((location) => location.country === selectedCountryOption).map((location) => location.state)
        )
      ];
      setAPIKey('state');
      setStateOptions(filteredStates);
      const defaultState = filteredStates[0] || '';
      setSelectedStateOption(defaultState);

      let defaultCity = '';
      if (!defaultState) {
        // Update cities based on the selected country if there is no state
        const filteredCities = [
          ...new Set(
            locations.filter((location) => location.country === selectedCountryOption).map((location) => location.city)
          )
        ];
        setAPIKey('city');
        setCityOptions(filteredCities);
        defaultCity = filteredCities[0] || '';
        setSelectedCityOption(defaultCity);
      }

      // update the form values, only if the keys exist on the form
      const updatedFormData: any = {};
      if (Object.keys(formData).includes('state')) updatedFormData.state = defaultState;
      if (Object.keys(formData).includes('city')) updatedFormData.city = defaultCity;
      setFormData({ ...formData, ...updatedFormData });
    }
  }, [selectedCountryOption]);

  // Update cities based on selected state
  useEffect(() => {
    if (selectedStateOption && locations) {
      // Update cities based on the selected state
      const filteredCities = [
        ...new Set(
          locations.filter((location) => location.state === selectedStateOption).map((location) => location.city)
        )
      ];
      setAPIKey('city');
      setCityOptions(filteredCities);
      const defaultCity = filteredCities[0] || '';
      setFormValueIfKeyExists(defaultCity, 'city');
      setSelectedCityOption(defaultCity);
    }
  }, [selectedStateOption]);

  useEffect(() => {
    if (selectedCityOption && locations) {
      validateForm();
    }
  }, [selectedCityOption]);

  useEffect(() => {
    if (surveyError && locationsLoading) {
      setLocationsLoading(false);
      setLocationFieldsDisabled(false);
      resetError();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyError]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // Handles the keyboard key click -- changes input accordingly
  const onClick = (e: React.MouseEvent<HTMLButtonElement>, v: string) => {
    if (selectedField && selectedField.inputType === 'phone') {
      if (v === 'delete') {
        setPhoneNumberInput((prevState) => ({
          ...prevState,
          keyboard: prevState.keyboard.slice(0, prevState.keyboard.length - 1)
        }));
      } else if (v === 'clear') {
        setPhoneNumberInput((prevState) => ({ ...prevState, keyboard: '' }));
      } else {
        setPhoneNumberInput((prevState) => ({ ...prevState, keyboard: prevState.keyboard + v })); // Forward to phone number component
      }
    } else {
      const currentFieldValue = String(formData[apiKey] || '');

      if (v === 'delete') {
        setFormData((prev) => ({
          ...prev,
          [apiKey]: currentFieldValue.slice(0, currentFieldValue.length - 1)
        }));
      } else if (v === 'clear') {
        setFormData((prev) => ({ ...prev, [apiKey]: '' }));
      } else {
        if (selectedField && selectedField.maxChar) {
          if (selectedField.maxChar > currentFieldValue.length) {
            setFormData((prev) => ({ ...prev, [apiKey]: currentFieldValue + v }));
          }
        } else {
          setFormData((prev) => ({ ...prev, [apiKey]: currentFieldValue + v }));
        }
      }
    }
  };

  const onSuggestionClick = (e: React.MouseEvent<HTMLButtonElement>, v: string) => {
    if (selectedField && selectedField.keyboard === 'country') {
      setFormData({ ...formData, [apiKey]: v });
    } else {
      setFormData({ ...formData, [apiKey]: formData[apiKey] + v });
    }
  };

  // Handle which input ref has the focus
  const handleFocus = (item: SurveyField, index: number) => {
    setSelectedField(item);
    setAPIKey(item.apiParam);
    setInputIndex(index);
  };

  const debouncedGetLocations = debounce((postalCode: string) => {
    getLocations({ postalCode });
  }, 300); // Adjust the debounce delay as needed

  const handleBlur = (item: SurveyField) => {
    setSelectedField(null);
    handleControlsHide();
    // Clear previous data and states
    setCountryOptions([]);
    setStateOptions([]);
    setCityOptions([]);
    setSelectedCountryOption('');
    setSelectedStateOption('');
    setSelectedCityOption('');
    setLocationsLoading(false);

    // reset the form values, only if the keys exist on the form
    const updatedFormData: any = {};
    if (Object.keys(formData).includes('country')) updatedFormData.country = '';
    if (Object.keys(formData).includes('state')) updatedFormData.state = '';
    if (Object.keys(formData).includes('city')) updatedFormData.city = '';
    setFormData({ ...formData, ...updatedFormData });

    // validate the input value and call getLocations
    if (formData[item.apiParam]) {
      const validateInputError = validateInput(item, formData);
      const validateInputTypeError = validateInputType(formData[item.apiParam], item.inputType || '');

      if (!validateInputError && !validateInputTypeError) {
        setLocationsLoading(true);
        debouncedGetLocations(formData[item.apiParam]);
      }
    }
  };

  const handleControlsHide = () => {
    setTimeout(() => {
      setHideScrollBar(false);
      setInputControl('');
      validateForm();
    }, 0);
  };

  const isValidInput = (item: string, type: string, key: string) => {
    if (type === 'phone') {
      const { keyboard, selectedCountry } = phoneNumberInput;
      if (keyboard && selectedCountry && keyboard.length < selectedCountry.size) {
        setError((prevState) => ({
          ...prevState,
          [key]: i18n.t('validation.phoneMinDigitsError', { phoneMinDigits: selectedCountry && selectedCountry.size })
        }));
        setValidation(false);
        return false;
      }
    } else {
      const errorMsg: any = validateInputType(item, type, countries as Country[]);
      if (errorMsg) {
        setError((prevState) => ({ ...prevState, [key]: errorMsg }));
        setValidation(false);
        return false;
      }
    }
    return true;
  };

  const validate = (currIndex: number) => {
    const currItem = displayFields[currIndex];
    const errorStr = validateInput(currItem, formData);

    if (errorStr) {
      setError((prevState) => ({ ...prevState, [currItem.apiParam]: errorStr }));
      setValidation(false);
      return false;
    } else if (formData[currItem.apiParam] && currItem.inputType) {
      return isValidInput(formData[currItem.apiParam], currItem.inputType, currItem.apiParam);
    }

    return true;
  };

  const validateForm = () => {
    let validCount = 0;
    let totalCount = 0;

    displayFields.forEach((_item, index) => {
      const valid = validate(index);
      if (valid) validCount++;
      if (validCount === displayFields.length) {
        setValidation(true);
      } else {
        setValidation(false);
      }
      totalCount++;
      if (totalCount === displayFields.length) {
        setValidationComplete(true);
      }
    });
  };

  /**
   * Handles navigation from the keyboard, switching between fields
   * or hiding the keyboard if no more fields are available.
   */
  const onKeyboardNavigationClick = (index: number, direction: string) => {
    // validate the current field
    validate(index);

    // get the new field to focus, whether it is next or back
    let newDisplayFieldIndex;
    if (direction === 'next') {
      newDisplayFieldIndex = getNextActiveDisplayFieldIndex(index);
    } else {
      newDisplayFieldIndex = getPreviousActiveDisplayFieldIndex(index);
    }
    const newDisplayField = displayFields[newDisplayFieldIndex];

    if (newDisplayField && formRefs.current[newDisplayField.apiParam]) {
      // if another displayField: change focus to it
      setAPIKey(newDisplayField.apiParam);
      setSelectedField(newDisplayField);
      setInputIndex(newDisplayFieldIndex);
    } else {
      // else: hide the keyboard
      handleControlsHide();
    }
  };

  const handleKeyBoardVisibility = (type = 'alphaNumeric') => {
    if (type === 'numeric' || type === 'date') {
      setInputControl('numpad');
    } else {
      setInputControl('keyboard');
    }
    setHideScrollBar(true);
  };

  /**
   * Returns the index of the next active display field.
   * It skips over fields that are either not present in formRefs or are disabled.
   */
  const getNextActiveDisplayFieldIndex = (index: number) => {
    let tempIndex = index;
    let nextDisplayField;
    do {
      tempIndex++;
      nextDisplayField = displayFields[tempIndex];
    } while (
      tempIndex < displayFields.length &&
      (!formRefs.current[nextDisplayField.apiParam] || formRefs.current[nextDisplayField.apiParam]?.disabled)
    );

    return tempIndex;
  };

  /**
   * Returns the index of the previous active display field.
   * It skips over fields that are either not present in formRefs or are disabled.
   */
  const getPreviousActiveDisplayFieldIndex = (index: number) => {
    let tempIndex = index;
    let prevDisplayField;
    do {
      tempIndex--;
      prevDisplayField = displayFields[tempIndex];
    } while (
      tempIndex > 0 &&
      (!formRefs.current[prevDisplayField.apiParam] || formRefs.current[prevDisplayField.apiParam]?.disabled)
    );

    return tempIndex;
  };

  const onPrevPageClick = () => {
    resetLocations();
    navigate('/ticketing/cart/customer', { state: { from: location.pathname } });
  };

  const onNextPageClick = () => {
    resetLocations();
    // if all fields are not required user can skip and continue the purchase flow
    if (skipAndContinue && emptySurvey) {
      navigate('/ticketing/cart/delivery-selection');
    } else {
      if (validation) {
        const payload = getSurveyPayload();
        saveSurvey({ details: payload });
      } else {
        validateForm();
        const errStr = Object.values(error).find((item) => item.length);
        if (errStr) {
          const idx = Object.values(error).findIndex((item) => item === errStr);
          handleFocus(displayFields[idx], idx);
        }
      }
    }
  };

  const getNextTextForKeyboard = (idx: number) => {
    let nextLabel = t('commonText.next');

    const nextDisplayFieldIndex = getNextActiveDisplayFieldIndex(idx);
    const nextDisplayField = displayFields[nextDisplayFieldIndex];
    if (!nextDisplayField || !formRefs.current[nextDisplayField.apiParam]) {
      nextLabel = t('commonText.done');
    }

    return nextLabel;
  };

  const getLocalizedText = (textObj: LocalizedStrRecord = {}, apiParam?: string): string => {
    return get(textObj, `${i18n.language}`) || t(`inputFieldLabel.${apiParam}`);
  };

  /**
   * Returns the component to use as the label for an input field.
   *
   * There is an error when passing `label` to InputField if it is a child of InputMask.
   * The workaround is to render the label separately, which requires additional
   * styles to match what is applied to the `label` of other InputField(s).
   */
  const getLabelText = (surveyField: SurveyField) => {
    const { required, label, apiParam, inputMask } = surveyField;

    const typographyStyles = inputMask ? { padding: '0 24px', margin: '0 0 10px' } : {};

    return (
      <Typography fontSize={24} fontWeight={600} style={typographyStyles}>
        {required ? <span style={{ color: 'red' }}>* </span> : ''}
        {getLocalizedText(label, apiParam)}
      </Typography>
    );
  };

  const getSurveyPayload = () => {
    const payload: { [k: string]: any } = {};
    displayFields.forEach((surveyItem) => {
      if (surveyItem.inputType === 'countryName' && formData[surveyItem.apiParam]) {
        const selectedCountry =
          countries && countries.find((country) => country.description === formData[surveyItem.apiParam]);
        const countryId = selectedCountry && selectedCountry.id.toString();
        payload[surveyItem.apiParam] = countryId;
      } else {
        if (surveyItem.inputType === 'header') {
          payload[surveyItem.apiParam] = getLocalizedText(surveyItem.label);
        } else if (surveyItem.inputType === 'date' && formData[surveyItem.apiParam]) {
          const value = formData[surveyItem.apiParam];
          const tempVal = `${value.substring(0, 2)}/${value.substring(2, 4)}/${value.substring(4)}`;
          payload[surveyItem.apiParam] = tempVal;
        } else {
          payload[surveyItem.apiParam] = formData[surveyItem.apiParam];
        }
      }
    });

    return payload;
  };

  const escapeRegexCharacters = (value: string) => {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const countrySuggestion = (value: string) => {
    const escapedValue = escapeRegexCharacters(value.trim());
    if (escapedValue === '') {
      return [];
    }
    const regex = new RegExp(`\\b${escapedValue}`, 'i');
    const filteredCountries = countries && countries.filter((country) => regex.test(country.description));

    return filteredCountries && filteredCountries.slice(0, 4);
  };

  const getSuggestions = () => {
    let result;
    const formDataValue = formData[apiKey];
    if (selectedField && selectedField.keyboard === 'email') {
      result = emailSuggestions;
    } else if (selectedField && selectedField.keyboard === 'country') {
      const suggestedCountries = countrySuggestion(formDataValue);
      const mappedValues = suggestedCountries && suggestedCountries.map((country) => ({ value: country.description }));

      result = mappedValues || [];
    }

    return result;
  };

  const handleResetError = () => {
    if (surveyError) {
      resetError();
    }
    if (countryError) {
      getCountries();
    }
  };

  const setPhoneNumber = (phoneNumber: string, phNumInp: typeof phoneNumberInput, apiParam: string) => {
    setFormData((prev) => ({
      ...prev,
      [apiParam]: phoneNumber
    }));

    setPhoneNumberInput(phNumInp);
    setPhoneCountry(phNumInp.selectedCountry);
  };

  const setFormValueIfKeyExists = (value: string, apiParam: string) => {
    if (Object.keys(formData).length && Object.keys(formData).includes(apiParam)) {
      setFormData({ ...formData, [apiParam]: value });
    }
  };

  const handleSelectCountryChange = (value: string, item: SurveyField) => {
    setSelectedCountryOption(value);
    setFormValueIfKeyExists(value, item.apiParam);
  };
  const handleSelectStateChange = (value: string, item: SurveyField) => {
    setSelectedStateOption(value);
    setFormValueIfKeyExists(value, item.apiParam);
  };
  const handleSelectCityChange = (value: string, item: SurveyField) => {
    setSelectedCityOption(value);
    setFormValueIfKeyExists(value, item.apiParam);
  };

  const renderStandardTextInput = (field: SurveyField, idx: number, isDisabled: boolean) => {
    return (
      <InputField
        size="large"
        fullWidth
        type="text"
        name={field.apiParam}
        label={getLabelText(field)}
        placeholder={getLocalizedText(field.placeholder ? field.placeholder : field.label, field.apiParam)}
        value={formData[field.apiParam]}
        error={Boolean(error[field.apiParam])}
        onFocus={(_e) => {
          if (isDisabled) return;
          handleFocus(field, idx);
        }}
        onClick={(_e) => {
          if (isDisabled) return;
          setInputIndex(idx);
          handleKeyBoardVisibility(field.keyboard);
        }}
        sx={{ height: '80px' }}
        className={classes.surveyInputField}
        disabled={isDisabled}
        inputRef={(el: HTMLInputElement | null) => {
          formRefs.current[field.apiParam] = el;
        }}
      />
    );
  };

  const renderPostalLookupFields = (
    field: SurveyField,
    fieldIndex: number,
    options: string[],
    onChange: (value: string, item: SurveyField) => void
  ) => {
    return (
      <>
        <Box sx={{ position: 'relative' }}>
          {options.length > 1 ? (
            <Grid sx={{ marginTop: '4px' }}>
              <SelectDropdown
                gridProps={{ sx: { height: '80px', padding: '0 56px' } }}
                label={getLabelText(field)}
                options={options}
                onChange={onChange}
                item={field}
              />
            </Grid>
          ) : (
            renderStandardTextInput(field, fieldIndex, locationFieldsDisabled)
          )}
          {locationsLoading && renderCircularProgress({ right: '50px', bottom: '22px' })}
        </Box>
        {renderFieldError(field)}
      </>
    );
  };

  const renderCircularProgress = (customStyles: any) => (
    <CircularProgress
      color="inherit"
      size={36}
      style={{
        position: 'absolute',
        color: color.GREEN,
        ...customStyles
      }}
    />
  );

  /**
   * Render a red error message IF the field has an error defined.
   */
  const renderFieldError = (field: SurveyField) => (
    <>
      {error[field.apiParam] && error[field.apiParam].length && (
        <Typography color={color.RED} fontSize={textSize.kiosk.SMALL} sx={{ padding: '0 0 0 58px' }}>
          {error[field.apiParam]}
        </Typography>
      )}
    </>
  );

  const renderFormFields = (surveyInput: SurveyField, idx: number) => {
    switch (surveyInput.inputType) {
      case 'header':
        return (
          <Box fullWidth>
            <Typography className={classes.surveyInputLabel}>{getLocalizedText(surveyInput.label)}</Typography>
          </Box>
        );
      case 'phone':
        return (
          <Box fullWidth>
            <PhoneNumberInput
              ref={(el: HTMLInputElement | null) => {
                formRefs.current[surveyInput.apiParam] = el;
              }}
              gridProps={{
                sx: { height: '80px' }
              }}
              textInputProps={{
                name: 'value',
                placeholder: getLocalizedText(
                  surveyInput.placeholder ? surveyInput.placeholder : surveyInput.label,
                  surveyInput.apiParam
                ),
                fullWidth: true
              }}
              label={getLabelText(surveyInput)}
              inputModel={phoneNumberInput}
              setPhoneNumber={(phoneNumber, number) => setPhoneNumber(phoneNumber, number, surveyInput.apiParam)}
              onFocus={() => handleFocus(surveyInput, idx)}
              onClick={() => {
                setInputIndex(idx);
                handleKeyBoardVisibility(surveyInput.keyboard);
              }}
              error={error[surveyInput.apiParam] || undefined}
            />
          </Box>
        );

      case 'lookupPostalCode':
        return (
          <>
            <Box sx={{ position: 'relative' }}>
              <InputField
                size="large"
                fullWidth
                type="text"
                name={surveyInput.apiParam}
                label={getLabelText(surveyInput)}
                placeholder={getLocalizedText(
                  surveyInput.placeholder ? surveyInput.placeholder : surveyInput.label,
                  surveyInput.apiParam
                )}
                value={formData[surveyInput.apiParam]}
                error={Boolean(error[surveyInput.apiParam])}
                onFocus={(_e) => handleFocus(surveyInput, idx)}
                onBlur={(_e) => handleBlur(surveyInput)}
                onClick={(_e) => {
                  setInputIndex(idx);
                  handleKeyBoardVisibility(surveyInput.keyboard);
                }}
                sx={{ height: '80px' }}
                className={classes.surveyInputField}
                inputRef={(el: HTMLInputElement | null) => {
                  formRefs.current[surveyInput.apiParam] = el;
                }}
              />
              {locations && locations.length > 0 && (
                <Icon
                  name="CheckCircleOutline"
                  sx={{
                    color: color.GREEN,
                    fontSize: '36px',
                    position: 'absolute',
                    right: '50px',
                    bottom: '22px'
                  }}
                />
              )}
            </Box>
            {renderFieldError(surveyInput)}
          </>
        );

      case 'autoCountry':
        return renderPostalLookupFields(surveyInput, idx, countryOptions, handleSelectCountryChange);

      case 'autoState':
        return renderPostalLookupFields(surveyInput, idx, stateOptions, handleSelectStateChange);

      case 'autoCity':
        return renderPostalLookupFields(surveyInput, idx, cityOptions, handleSelectCityChange);

      default:
        return (
          <>
            {surveyInput.inputMask ? (
              <>
                {getLabelText(surveyInput)}
                <InputMask
                  mask={surveyInput.inputMask}
                  value={formData[surveyInput.apiParam] || ''}
                  onFocus={() => handleFocus(surveyInput, idx)}
                  name={surveyInput.apiParam}
                  maskPlaceholder={getLocalizedText(
                    surveyInput.placeholder ? surveyInput.placeholder : surveyInput.label,
                    surveyInput.apiParam
                  )}
                  onClick={(_e) => {
                    setInputIndex(idx);
                    handleKeyBoardVisibility(surveyInput.keyboard);
                  }}
                >
                  <InputField
                    size="large"
                    fullWidth
                    type="text"
                    inputProps={{ maxLength: 8 }}
                    placeholder={getLocalizedText(
                      surveyInput.placeholder ? surveyInput.placeholder : surveyInput.label,
                      surveyInput.apiParam
                    )}
                    error={Boolean(error[surveyInput.apiParam])}
                    sx={{ height: '80px' }}
                    className={classes.surveyInputField}
                    inputRef={(el: HTMLInputElement | null) => {
                      formRefs.current[surveyInput.apiParam] = el;
                    }}
                  />
                </InputMask>
              </>
            ) : (
              renderStandardTextInput(surveyInput, idx, false)
            )}
            {renderFieldError(surveyInput)}
          </>
        );
    }
  };

  const hasRequiredCountryField = displayFields.find((field) => field.inputType === 'countryName' && field.required);

  if (uiLoading) return <LoadingOverlay style={{ zIndex: '99' }} />;

  return (
    <>
      {((surveyError && !locationsLoading) || (hasRequiredCountryField && countryError)) && !loading && (
        <ActionOverlay
          height={600}
          title={
            <Box margin="0 0 5" width={720}>
              <Typography fontSize={64} fontWeight={600} align="center" sx={{ lineHeight: 1 }}>
                {t('commonText.somethingWentWrong')}
              </Typography>
            </Box>
          }
          description={
            <Box width={810}>
              <Typography fontSize={30} align="center">
                {surveyError}
              </Typography>
            </Box>
          }
          confirmText={t('commonText.tryAgain')}
          onConfirm={() => {
            handleResetError();
          }}
        />
      )}
      <Grid container alignContent="space-between" className={classes.surveyViewOuterContainer}>
        <Grid container item alignContent="space-between" className={classes.surveyViewInnerContainer}>
          {displayFields && displayFields.length ? (
            <>
              <Box width="100%" flexOverride="none" margin="0 0 6">
                <Typography fontSize={64} fontWeight={600} align="center">
                  {header}
                </Typography>
                <Typography fontSize={30} align="center">
                  {subheader}
                </Typography>
              </Box>
              <MuiBox className={classes.surveyViewInputsBox}>
                <Scrollbar hideScrollBar={hideScrollBar || displayFields.length < 3}>
                  <FlexBox direction="column" gutter={3} noWrap flexOverride="1">
                    <Grid container alignContent="flex-start" className={classes.surveyViewInputsContainer}>
                      {displayFields.map((surveyInput, idx) => (
                        <Grid
                          item
                          xs={surveyInput.halfWidth ? 6 : 12}
                          sx={
                            surveyInput.inputType === 'header' ? { padding: '20px 20px 0' } : { padding: '10px 20px' }
                          }
                        >
                          {renderFormFields(surveyInput, idx)}
                        </Grid>
                      ))}
                    </Grid>
                  </FlexBox>
                </Scrollbar>
              </MuiBox>
            </>
          ) : (
            <FlexBox sx={{ height: '650px' }} justify="center" align="center">
              <Typography fontSize={30}>{t('commonText.nothingToDisplay')}</Typography>
            </FlexBox>
          )}
        </Grid>
      </Grid>

      {/* Footer */}
      {!inputControl && (
        <Footer
          onPrevClick={onPrevPageClick}
          onNextClick={onNextPageClick}
          isNextDisabled={!validation || locationsLoading}
          nextText={nextText}
          skipNavPrompt={Boolean(surveyError || (hasRequiredCountryField && countryError))}
        />
      )}

      {/* Keyboard */}
      {inputControl === 'keyboard' && (
        <AbsoluteBox raised fullWidth bg="x-light-gray" height="480px" bottom="0" left="0">
          <Grid>
            <Keyboard
              language={i18n.language}
              onClick={onClick}
              onNext={() => onKeyboardNavigationClick(inputIndex, 'next')}
              onPrev={() => onKeyboardNavigationClick(inputIndex, 'back')}
              nextText={getNextTextForKeyboard(inputIndex)}
              prevText={inputIndex ? t('commonText.previous') : t('commonText.back')}
              onCaretClick={(_e) => handleControlsHide()}
              onClickOutside={(_e) => handleControlsHide()}
              suggestions={getSuggestions()}
              onSuggestionClick={onSuggestionClick}
            />
          </Grid>
        </AbsoluteBox>
      )}

      {inputControl === 'numpad' && (
        <AbsoluteBox raised fullWidth bg="x-light-gray" bottom="0" left="0">
          <Grid>
            <NumPadAction
              onClick={onClick}
              onConfirm={() => onKeyboardNavigationClick(inputIndex, 'next')}
              onBack={() => onKeyboardNavigationClick(inputIndex, 'back')}
              onCaretClick={(_e) => handleControlsHide()}
              onClickOutside={(_e) => handleControlsHide()}
              layout="row"
              confirmConfig={{
                label: getNextTextForKeyboard(inputIndex),
                backLabel: inputIndex ? t('commonText.previous') : t('commonText.back'),
                customSize: 'x-large',
                width: 475,
                appearance: 'primary'
              }}
            />
          </Grid>
        </AbsoluteBox>
      )}
    </>
  );
};

export default SurveyView;
